import React, { useState, useEffect, useMemo } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import {
    Paper, Box, Typography, Stack, Divider, Button, Chip, Link
} from '@mui/material';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import SportsHockeyIcon from '@mui/icons-material/SportsHockey';
import { CalendarEvent, TournamentStanding } from '../types';
import {
    TournamentInfo,
    getTournamentLink,
    parseLocalDate,
    getDayDiff,
    getActiveOrUpcomingTournament
} from '../tournaments';
import { fetchTournamentDetails, TournamentDetails } from '../utils/tournamentScraper';
import { formatLocation, getEventType, EventType, getLocalDateString } from '../utils/eventUtils';
import { logEvent } from '../analytics';
import { CURRENT_SEASON_KEY } from '../seasons';
import { useRoster } from '../RosterContext';
import { calculateTournamentRank } from '../utils/advancementCalculator';

export interface TournamentCardProps {
    tournament?: TournamentInfo | null;
    seasonKey?: string;
    calendarEvents?: CalendarEvent[];
    currentDate?: Date;
    onLinkClick?: () => void;
    tournamentDetails?: TournamentDetails | null;
}

export const TournamentCard: React.FC<TournamentCardProps> = ({
    tournament: explicitTournament,
    seasonKey = CURRENT_SEASON_KEY,
    calendarEvents = [],
    currentDate = new Date(),
    onLinkClick,
    tournamentDetails
}) => {
    const { isAuthReady } = useRoster();
    const [details, setDetails] = useState<TournamentDetails | null>(tournamentDetails || null);

    useEffect(() => {
        if (tournamentDetails) {
            setDetails(tournamentDetails);
        }
    }, [tournamentDetails]);

    // Resolve tournament (either passed explicitly or auto-detected for the season)
    const activeOrUpcoming = useMemo(() => {
        if (explicitTournament) {
            const today = new Date(currentDate.getFullYear(), currentDate.getMonth(), currentDate.getDate());
            const start = parseLocalDate(explicitTournament.startDate);
            const end = parseLocalDate(explicitTournament.endDate);
            end.setHours(23, 59, 59, 999);
            const isOngoing = currentDate >= start && currentDate <= end;
            const daysUntil = getDayDiff(today, start);
            return {
                tournament: explicitTournament,
                isOngoing,
                daysUntil
            };
        }
        return getActiveOrUpcomingTournament(seasonKey, currentDate);
    }, [explicitTournament, seasonKey, currentDate]);

    const tournament = activeOrUpcoming?.tournament;
    const isOngoing = activeOrUpcoming?.isOngoing ?? false;
    const daysUntil = activeOrUpcoming?.daysUntil ?? 0;

    // Fetch tournament schedule and standings when tournament is detected
    useEffect(() => {
        if (!tournament || !isAuthReady) {
            setDetails(null);
            return;
        }

        // Only fetch if within 14 days or ongoing
        if (!isOngoing && (daysUntil <= 0 || daysUntil > 14)) {
            return;
        }

        let isMounted = true;

        fetchTournamentDetails(tournament)
            .then(data => {
                if (isMounted) {
                    setDetails(data);
                }
            })
            .catch(err => {
                console.warn('Error fetching tournament details in TournamentCard:', err);
            });

        return () => {
            isMounted = false;
        };
    }, [tournament, isOngoing, daysUntil, isAuthReady]);

    // Check if tournament games are present in calendar events feed
    const hasCalendarTournamentGames = useMemo(() => {
        if (!tournament || !calendarEvents || calendarEvents.length === 0) return false;
        return calendarEvents.some(event => {
            const eventDateStr = getLocalDateString(event.start);
            const inDateWindow = eventDateStr >= tournament.startDate && eventDateStr <= tournament.endDate;
            const type = getEventType(event);
            const isGame = type === EventType.Game || type === EventType.Exhibition || type === EventType.Tournament;
            return inDateWindow && isGame;
        });
    }, [tournament, calendarEvents]);

    // Determine if schedule is available
    const isScheduleAvailable = useMemo(() => {
        if (details?.isScheduleAvailable) return true;
        if (details?.games && details.games.length > 0) return true;
        if (hasCalendarTournamentGames) return true;
        return false;
    }, [details, hasCalendarTournamentGames]);

    // Extract Jets record and standings summary for ongoing tournament
    const jetsSummary = useMemo(() => {
        if (!details) return null;

        // 1. Try to find Jets in tournament standings
        let jetsStanding: TournamentStanding | null = null;
        let poolName = 'Pool Standings';

        if (details.standings && details.standings.length > 0) {
            for (const pool of details.standings) {
                const found = pool.standings.find(s => s.teamName.toLowerCase().includes('jets'));
                if (found) {
                    jetsStanding = found;
                    poolName = pool.poolName;
                    break;
                }
            }
        }

        if (jetsStanding) {
            const rawSeed = jetsStanding.seed?.trim();
            const seed = rawSeed && rawSeed !== '-' && rawSeed !== 'N/A' && rawSeed !== '' ? rawSeed : null;
            const rankInfo = calculateTournamentRank(details.standings);
            const hasAnyGamesPlayed = details.standings.some(p => p.standings.some(s => (parseInt(s.gp, 10) || 0) > 0)) ||
                Boolean(details.games && details.games.some(g => g.status === 'final' || (g.homeScore !== '-' && g.homeScore !== '')));

            return {
                wins: parseInt(jetsStanding.w, 10) || 0,
                losses: parseInt(jetsStanding.l, 10) || 0,
                ties: parseInt(jetsStanding.t, 10) || 0,
                otw: parseInt(jetsStanding.otw, 10) || 0,
                otl: parseInt(jetsStanding.otl, 10) || 0,
                points: parseInt(jetsStanding.pts, 10) || 0,
                seed,
                rank: rankInfo?.currentRank ?? null,
                totalTeams: rankInfo?.totalTeams ?? null,
                hasAnyGamesPlayed,
                poolName,
                goalsFor: parseInt(jetsStanding.gf, 10) || 0,
                goalsAgainst: parseInt(jetsStanding.ga, 10) || 0,
                goalDiff: jetsStanding.gd || '0',
                gamesPlayed: parseInt(jetsStanding.gp, 10) || 0,
                source: 'standings' as const
            };
        }

        // 2. Compute directly from games if standings are empty
        if (details.games && details.games.length > 0) {
            const jetsGames = details.games.filter(g =>
                g.homeTeam.toLowerCase().includes('jets') || g.visitorTeam.toLowerCase().includes('jets')
            );

            let wins = 0;
            let losses = 0;
            let ties = 0;
            let goalsFor = 0;
            let goalsAgainst = 0;
            let completedGamesCount = 0;

            for (const game of jetsGames) {
                if (game.homeScore !== '-' && game.visitorScore !== '-') {
                    const hScore = parseInt(game.homeScore, 10) || 0;
                    const vScore = parseInt(game.visitorScore, 10) || 0;
                    const isHome = game.homeTeam.toLowerCase().includes('jets');
                    const jetsScore = isHome ? hScore : vScore;
                    const oppScore = isHome ? vScore : hScore;

                    completedGamesCount++;
                    goalsFor += jetsScore;
                    goalsAgainst += oppScore;

                    if (jetsScore > oppScore) wins++;
                    else if (jetsScore < oppScore) losses++;
                    else ties++;
                }
            }

            const points = wins * 3; // Standard 200x85 regulation win point value
            const gd = goalsFor - goalsAgainst;

            return {
                wins,
                losses,
                ties,
                otw: 0,
                otl: 0,
                points,
                seed: null,
                rank: null,
                totalTeams: null,
                hasAnyGamesPlayed: completedGamesCount > 0,
                poolName: 'Preliminary Round',
                goalsFor,
                goalsAgainst,
                goalDiff: gd > 0 ? `+${gd}` : `${gd}`,
                gamesPlayed: completedGamesCount,
                source: 'games' as const
            };
        }

        return null;
    }, [details]);

    // Find next upcoming tournament game
    const nextGame = useMemo(() => {
        if (!details?.games || details.games.length === 0) return null;
        const jetsGames = details.games.filter(g =>
            g.homeTeam.toLowerCase().includes('jets') || g.visitorTeam.toLowerCase().includes('jets')
        );
        return jetsGames.find(g => g.homeScore === '-' && g.visitorScore === '-') || null;
    }, [details]);

    // If no tournament or not in the 2-week window / ongoing, do not render
    if (!tournament || (!isOngoing && (daysUntil <= 0 || daysUntil > 14))) {
        return null;
    }

    const tournamentLink = getTournamentLink(tournament);

    // Format Countdown text
    let countdownText = '';
    if (isOngoing) {
        countdownText = 'Tournament in Progress';
    } else if (daysUntil === 1) {
        countdownText = 'Starts tomorrow';
    } else if (daysUntil === 0) {
        countdownText = 'Starts today';
    } else {
        countdownText = `Starts in ${daysUntil} days`;
    }

    const handleTournamentNavigation = () => {
        logEvent('navigate_tournament_card', {
            tournament_key: tournament.key,
            is_ongoing: isOngoing,
            days_until: daysUntil
        });
        if (onLinkClick) {
            onLinkClick();
        }
    };

    return (
        <Paper
            variant="outlined"
            elevation={0}
            data-testid="tournament-card"
            sx={{
                borderRadius: 2,
                mb: 3,
                overflow: 'hidden',
                borderColor: isOngoing ? 'primary.main' : 'divider',
                borderWidth: isOngoing ? 2 : 1,
                boxShadow: isOngoing ? '0 4px 12px rgba(25, 118, 210, 0.08)' : 'none'
            }}
        >
            {/* Card Header */}
            <Box sx={{ p: { xs: 2, sm: 3 }, pb: 2, bgcolor: isOngoing ? 'primary.50' : 'inherit' }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1.5}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <EmojiEventsIcon color={isOngoing ? 'primary' : 'action'} />
                        <Typography
                            variant="h5"
                            component="h1"
                            sx={{
                                fontSize: { xs: '1.25rem', sm: '1.5rem' },
                                fontWeight: isOngoing ? 700 : 600,
                                color: isOngoing ? 'primary.dark' : 'text.primary'
                            }}
                        >
                            {isOngoing ? 'Tournament' : 'Upcoming Tournament'}
                        </Typography>
                    </Stack>
                    {isOngoing ? (
                        <Chip
                            label="In Progress"
                            color="primary"
                            size="small"
                            sx={{ fontWeight: 700, textTransform: 'uppercase', height: 24, fontSize: '0.72rem' }}
                        />
                    ) : isScheduleAvailable ? (
                        <Chip
                            label="Schedule Available"
                            color="success"
                            size="small"
                            icon={<CheckCircleOutlineIcon fontSize="small" />}
                            sx={{ fontWeight: 600, height: 24, fontSize: '0.72rem' }}
                        />
                    ) : (
                        <Chip
                            label="Upcoming"
                            variant="outlined"
                            size="small"
                            color="primary"
                            sx={{ fontWeight: 600, height: 24, fontSize: '0.72rem' }}
                        />
                    )}
                </Stack>
            </Box>

            <Divider />

            {/* Card Content */}
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
                {/* Tournament Info Header (Logo + Title + Dates/Location) */}
                <Stack direction="row" spacing={2} alignItems="center">
                    {tournament.logo && (
                        <Box
                            component="img"
                            src={tournament.logo.startsWith('http') ? tournament.logo : process.env.PUBLIC_URL + tournament.logo}
                            alt={`${tournament.name} logo`}
                            sx={{
                                height: { xs: 44, sm: 52 },
                                width: 'auto',
                                maxWidth: 72,
                                objectFit: 'contain',
                                flexShrink: 0
                            }}
                            onError={(e) => { (e.currentTarget as HTMLElement).style.display = 'none'; }}
                        />
                    )}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                        <Typography
                            variant="h6"
                            component="h2"
                            sx={{
                                fontWeight: 700,
                                fontSize: { xs: '1.05rem', sm: '1.25rem' },
                                lineHeight: 1.3,
                                mb: 0.25
                            }}
                        >
                            <Link
                                component={RouterLink}
                                to={tournamentLink}
                                onClick={handleTournamentNavigation}
                                underline="hover"
                                color="inherit"
                            >
                                {tournament.name}
                            </Link>
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                            {tournament.dates} • {formatLocation(tournament.location)}
                        </Typography>
                    </Box>
                </Stack>

                {/* --- MODE 1: UPCOMING TOURNAMENT (within 14 days) --- */}
                {!isOngoing && (
                    <Box sx={{ mt: 2 }}>
                        {/* Countdown row */}
                        <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                            <AccessTimeIcon fontSize="small" color="primary" />
                            <Typography variant="body1" fontWeight="bold" color="primary.main">
                                {countdownText}
                            </Typography>
                        </Stack>

                        {/* Schedule Available Callout */}
                        {isScheduleAvailable ? (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    bgcolor: 'success.50',
                                    borderColor: 'success.light',
                                    borderRadius: 1.5
                                }}
                            >
                                <Stack direction="row" spacing={1.25} alignItems="flex-start">
                                    <CalendarMonthIcon color="success" sx={{ fontSize: '1.25rem', mt: 0.25 }} />
                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                        <Typography variant="body2" fontWeight="bold" color="success.dark">
                                            Game Schedule is Available
                                        </Typography>
                                        {details?.games && details.games.length > 0 && (
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.25 }}>
                                                {details.games.length} game{details.games.length === 1 ? '' : 's'} scheduled for the Jets.
                                                {nextGame && ` First matchup: ${nextGame.dateTime} vs ${nextGame.homeTeam.includes('Jets') ? nextGame.visitorTeam : nextGame.homeTeam}.`}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                            </Paper>
                        ) : (
                            <Paper
                                variant="outlined"
                                sx={{
                                    p: 1.5,
                                    bgcolor: 'grey.50',
                                    borderColor: 'grey.200',
                                    borderRadius: 1.5
                                }}
                            >
                                <Typography variant="body2" color="text.secondary">
                                    Official game schedule and bracket match-ups will be posted once announced by the tournament.
                                </Typography>
                            </Paper>
                        )}
                    </Box>
                )}

                {/* --- MODE 2: ACTIVE / ONGOING TOURNAMENT --- */}
                {isOngoing && (
                    <Box sx={{ mt: 2.5 }}>
                        {/* Record & Standings Summary Box */}
                        <Paper
                            variant="outlined"
                            sx={{
                                p: { xs: 1.5, sm: 2 },
                                bgcolor: 'grey.50',
                                borderRadius: 1.5,
                                borderColor: 'divider'
                            }}
                        >
                            <Stack
                                direction="row"
                                justifyContent="space-around"
                                alignItems="center"
                                divider={<Divider orientation="vertical" flexItem />}
                            >
                                {/* Record */}
                                <Box sx={{ textAlign: 'center', px: 1 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Record
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                                        {jetsSummary ? `${jetsSummary.wins}-${jetsSummary.losses}-${jetsSummary.ties}` : '0-0-0'}
                                    </Typography>
                                </Box>

                                {/* Points */}
                                <Box sx={{ textAlign: 'center', px: 1 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Points
                                    </Typography>
                                    <Typography
                                        variant="h6"
                                        fontWeight="bold"
                                        color="primary.main"
                                        sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
                                    >
                                        {jetsSummary ? `${jetsSummary.points} PTS` : '0 PTS'}
                                    </Typography>
                                </Box>

                                {/* Current Standing / Seed */}
                                <Box sx={{ textAlign: 'center', px: 1 }}>
                                    <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                                        Standing
                                    </Typography>
                                    <Typography variant="h6" fontWeight="bold" sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}>
                                        {(() => {
                                            if (jetsSummary?.seed) {
                                                return jetsSummary.seed.startsWith('#') ? jetsSummary.seed : `Seed #${jetsSummary.seed}`;
                                            }
                                            if (jetsSummary?.rank) {
                                                if (jetsSummary.gamesPlayed === 0 && !jetsSummary.hasAnyGamesPlayed) {
                                                    return 'Starts Today';
                                                }
                                                return jetsSummary.totalTeams && jetsSummary.totalTeams > 1
                                                    ? `#${jetsSummary.rank} of ${jetsSummary.totalTeams}`
                                                    : `#${jetsSummary.rank}`;
                                            }
                                            if (jetsSummary && jetsSummary.gamesPlayed === 0) {
                                                return 'Starts Today';
                                            }
                                            return 'Pool Standings';
                                        })()}
                                    </Typography>
                                </Box>
                            </Stack>
                        </Paper>

                        {/* Next / Upcoming Tournament Matchup */}
                        {nextGame && (
                            <Box sx={{ mt: 1.5, p: 1.25, bgcolor: 'primary.50', borderRadius: 1.5, border: '1px solid', borderColor: 'primary.light' }}>
                                <Stack direction="row" spacing={1} alignItems="center">
                                    <SportsHockeyIcon fontSize="small" color="primary" />
                                    <Box sx={{ minWidth: 0, flex: 1 }}>
                                        <Typography variant="caption" color="text.secondary" fontWeight="bold" sx={{ textTransform: 'uppercase' }}>
                                            Next Tournament Matchup
                                        </Typography>
                                        <Typography variant="body2" fontWeight="bold" color="primary.dark">
                                            {nextGame.dateTime} • {nextGame.homeTeam} vs {nextGame.visitorTeam}
                                        </Typography>
                                        {nextGame.location && (
                                            <Typography variant="caption" color="text.secondary">
                                                {formatLocation(nextGame.location)}
                                            </Typography>
                                        )}
                                    </Box>
                                </Stack>
                            </Box>
                        )}
                    </Box>
                )}
            </Box>

            {/* Bottom Action Footer */}
            <Divider />
            <Box
                sx={{
                    px: { xs: 2, sm: 3 },
                    py: 1.5,
                    bgcolor: 'grey.50',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1
                }}
            >
                <Typography variant="caption" color="text.secondary">
                    {isOngoing ? 'Track live scores, standings & tiebreakers' : 'View full tournament details, hotel & rinks'}
                </Typography>
                <Button
                    component={RouterLink}
                    to={tournamentLink}
                    variant={isOngoing ? 'contained' : 'outlined'}
                    size="small"
                    onClick={handleTournamentNavigation}
                >
                    {isOngoing ? 'View Standings & Games' : 'View Tournament'}
                </Button>
            </Box>
        </Paper>
    );
};

export default TournamentCard;
